/**
 * Блок «Численность населения по населённым пунктам» (US-11 FR-4).
 *
 * Список НП территории: наименование · численность · доля от итога, с
 * полосой, пропорциональной численности (FR-4.1). Сортировка — по
 * убыванию численности, кроме города районного значения: он идёт первой
 * строкой независимо от численности (FR-4.2) — по району Магжана
 * Жумабаева это г. Булаево с 594 жителями.
 *
 * Признак города приходит из данных (`isCity`, US-12 сценарий 13): по
 * коду КАТО город районного значения от села не отличается, поэтому без
 * признака список остаётся отсортированным по убыванию.
 *
 * Внизу — строка итога «Всего по району» (FR-4.3). Итог считается по
 * переданным строкам: подгонять его под соседний блок нельзя, у блоков
 * могут быть разные отчётные даты (FR-4.4).
 */
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { cn } from '../../../../lib/cn';

export interface SettlementRow {
  kato: number;
  name_ru: string;
  name_kz: string;
  population: number | null;
  /** Город районного значения — первой строкой (FR-4.2). */
  isCity?: boolean;
}

interface Props {
  items: SettlementRow[];
  /** Отчётная дата блока — своя (FR-4.4, FR-7.2). */
  periodLabel?: string | null;
}

const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);
const fmtPct = (v: number): string =>
  `${Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(v)}`;

/** Город районного значения — первым, остальные по убыванию численности. */
const ordered = (items: SettlementRow[]): SettlementRow[] =>
  [...items].sort((a, b) => {
    if (a.isCity !== b.isCity) return a.isCity ? -1 : 1;
    return (b.population ?? 0) - (a.population ?? 0);
  });

export const SettlementPopulation = ({ items, periodLabel }: Props) => {
  const { t, i18n } = useTranslation();
  const isKz = i18n.language === 'kz';

  const rows = ordered(items);
  const total = rows.reduce((sum, row) => sum + (row.population ?? 0), 0);
  const max = rows.reduce((m, row) => Math.max(m, row.population ?? 0), 0);

  return (
    <Card className="h-full shadow-card">
      <CardHeader className="flex-row items-baseline justify-between space-y-0">
        <CardTitle>{t('digitalPassport.settlements.title')}</CardTitle>
        {periodLabel != null && (
          <span
            className="text-sm text-text-muted"
            data-testid="settlements-period"
          >
            {periodLabel}
          </span>
        )}
      </CardHeader>
      <CardContent>
        <section data-testid="block-settlement-population">
          {rows.length === 0 ? (
            <p
              data-testid="block-placeholder"
              className="text-sm lowercase text-text-muted"
            >
              {t('common.noData')}
            </p>
          ) : (
            <>
              <div className="flex items-center gap-2 border-b border-surface-border pb-2 text-xs text-text-muted">
                <span className="min-w-0 flex-1 basis-2/5">
                  {t('digitalPassport.settlements.colName')}
                </span>
                <span className="flex-1 basis-2/5" aria-hidden="true" />
                <span className="w-16 shrink-0 text-right">
                  {t('digitalPassport.settlements.colPeople')}
                </span>
                <span className="w-14 shrink-0 text-right">
                  {t('digitalPassport.settlements.colShare')}
                </span>
              </div>

              <ul
                className="max-h-[360px] overflow-y-auto"
                data-testid="settlements-list"
              >
                {rows.map((row, index) => {
                  const widthPct =
                    row.population === null || max === 0
                      ? 0
                      : Math.max(4, (row.population / max) * 100);
                  const share =
                    row.population === null || total === 0
                      ? null
                      : (row.population / total) * 100;
                  return (
                    <li
                      key={row.kato}
                      data-testid={`settlement-row-${row.kato}`}
                      className={cn(
                        'flex items-center gap-2 rounded-md px-1 py-2 text-sm',
                        index % 2 === 1 && 'bg-surface-muted/60',
                      )}
                    >
                      <span className="min-w-0 flex-1 basis-2/5 truncate">
                        {isKz ? row.name_kz : row.name_ru}
                      </span>
                      <span className="flex-1 basis-2/5">
                        {row.population !== null && (
                          <span
                            className="block h-4 rounded-sm bg-chart-blue"
                            style={{ width: `${widthPct}%` }}
                            data-testid={`settlement-row-${row.kato}-bar`}
                          />
                        )}
                      </span>
                      <span className="w-16 shrink-0 text-right tabular-nums">
                        {row.population !== null ? fmt(row.population) : '—'}
                      </span>
                      <span className="w-14 shrink-0 text-right tabular-nums text-text-muted">
                        {share !== null ? fmtPct(share) : '—'}
                      </span>
                    </li>
                  );
                })}
              </ul>

              <div
                className="flex items-center justify-between border-t border-surface-border pt-3"
                data-testid="settlements-total"
              >
                <span className="text-sm font-bold">
                  {t('digitalPassport.settlements.totalRow')}
                </span>
                <span className="text-sm font-bold tabular-nums">
                  {fmt(total)}
                </span>
              </div>
            </>
          )}
        </section>
      </CardContent>
    </Card>
  );
};
